import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check, Shield } from 'lucide-react';

const CookieBanner = ({ forceShow = false, onClose }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      const preferences = JSON.parse(consent);
      setCookiePreferences(preferences);
    }
  }, []);

  useEffect(() => {
    if (forceShow) {
      setShowBanner(true);
      setShowSettings(false);
    }
  }, [forceShow]);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
    if (onClose) onClose();
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      functional: false,
      analytics: false,
    };
    setCookiePreferences(necessaryOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(necessaryOnly));
    setShowBanner(false);
    setShowSettings(false);
    if (onClose) onClose();
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
    setShowBanner(false);
    setShowSettings(false);
    if (onClose) onClose();
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setCookiePreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-3 md:p-6"
        >
          <div className="container-custom max-w-6xl mx-auto">
            {/* SOLID BACKGROUND - Ne providna */}
            <div className="bg-gradient-to-br from-zinc-950 via-black to-zinc-950 rounded-2xl md:rounded-3xl shadow-2xl border-2 border-luxury-gold/40 overflow-hidden">
              
              {/* Main Banner */}
              {!showSettings ? (
                <div className="p-4 md:p-8">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                    {/* Icon - Hidden on Mobile */}
                    <div className="hidden md:flex flex-shrink-0">
                      <div className="w-12 md:w-16 h-12 md:h-16 bg-luxury-gold rounded-full flex items-center justify-center">
                        <Cookie className="text-black" size={28} />
                      </div>
                    </div>

                    {/* Content - Compact for Mobile */}
                    <div className="flex-1">
                      <h3 className="text-lg md:text-2xl lg:text-3xl font-display font-bold text-white mb-2 md:mb-3 flex items-center">
                        <Shield className="mr-2 text-luxury-gold flex-shrink-0" size={20} />
                        <span className="md:hidden">Ihre Privatsphäre</span>
                        <span className="hidden md:inline">Wir respektieren Ihre Privatsphäre</span>
                      </h3>
                      <p className="text-xs md:text-base text-zinc-300 leading-relaxed mb-2 md:mb-4">
                        Wir verwenden Cookies für die beste Erfahrung. Notwendige Cookies sind erforderlich, optionale helfen uns die Website zu verbessern.
                      </p>
                      <p className="text-[10px] md:text-sm text-zinc-400">
                        Mehr in unserer{' '}
                        <a href="/datenschutz" className="text-luxury-gold hover:underline font-semibold">
                          Datenschutzerklärung
                        </a>
                      </p>
                    </div>

                    {/* Actions - Compact Stack for Mobile */}
                    <div className="flex flex-col gap-2 md:gap-3 w-full md:w-auto">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAcceptAll}
                        className="btn-primary rounded-full px-5 md:px-8 py-2 md:py-3 text-sm md:text-base font-semibold whitespace-nowrap flex items-center justify-center"
                      >
                        <Check className="mr-1.5 md:mr-2" size={16} />
                        Alle akzeptieren
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAcceptNecessary}
                        className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-full px-5 md:px-8 py-2 md:py-3 text-sm md:text-base font-semibold whitespace-nowrap transition-colors"
                      >
                        Nur Notwendige
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowSettings(true)}
                        className="border border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-black rounded-full px-5 md:px-8 py-2 md:py-3 text-sm md:text-base font-semibold whitespace-nowrap transition-all flex items-center justify-center"
                      >
                        <Settings className="mr-1.5 md:mr-2" size={16} />
                        Einstellungen
                      </motion.button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Settings Panel - Scrollable on Mobile */
                <div className="p-4 md:p-8 max-h-[45vh] md:max-h-none overflow-y-auto">
                  <div className="flex items-center justify-between mb-4 md:mb-6 sticky top-0 bg-gradient-to-b from-black to-transparent pb-2">
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-display font-bold text-white flex items-center">
                      <Settings className="mr-2 md:mr-3 text-luxury-gold" size={24} />
                      Cookie-Einstellungen
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="text-zinc-400 hover:text-white transition-colors flex-shrink-0"
                    >
                      <X size={24} />
                    </button>
                  </div>

                  <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                    {/* Necessary Cookies */}
                    <div className="bg-zinc-900/80 rounded-xl md:rounded-2xl p-3 md:p-5 border border-luxury-gold/20">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="text-sm md:text-lg font-bold text-white mb-1 md:mb-2">
                            Notwendige Cookies
                          </h4>
                          <p className="text-[10px] md:text-sm text-zinc-400">
                            Erforderlich für Grundfunktionen. Können nicht deaktiviert werden.
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-10 md:w-12 h-5 md:h-6 bg-luxury-gold rounded-full flex items-center justify-end px-0.5 md:px-1">
                            <div className="w-3.5 md:w-4 h-3.5 md:h-4 bg-white rounded-full" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Functional Cookies */}
                    <div className="bg-zinc-900/80 rounded-xl md:rounded-2xl p-3 md:p-5 border border-luxury-gold/20">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="text-sm md:text-lg font-bold text-white mb-1 md:mb-2">
                            Funktionale Cookies
                          </h4>
                          <p className="text-[10px] md:text-sm text-zinc-400">
                            Erweiterte Funktionen wie Sprachauswahl und Einstellungen.
                          </p>
                        </div>
                        <button
                          onClick={() => togglePreference('functional')}
                          className="flex-shrink-0"
                        >
                          <div className={`w-10 md:w-12 h-5 md:h-6 rounded-full flex items-center transition-all ${
                            cookiePreferences.functional 
                              ? 'bg-luxury-gold justify-end' 
                              : 'bg-zinc-700 justify-start'
                          } px-0.5 md:px-1`}>
                            <div className="w-3.5 md:w-4 h-3.5 md:h-4 bg-white rounded-full" />
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="bg-zinc-900/80 rounded-xl md:rounded-2xl p-3 md:p-5 border border-luxury-gold/20">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4 className="text-sm md:text-lg font-bold text-white mb-1 md:mb-2">
                            Analyse Cookies
                          </h4>
                          <p className="text-[10px] md:text-sm text-zinc-400">
                            Helfen uns die Website-Nutzung zu verstehen und zu verbessern.
                          </p>
                        </div>
                        <button
                          onClick={() => togglePreference('analytics')}
                          className="flex-shrink-0"
                        >
                          <div className={`w-10 md:w-12 h-5 md:h-6 rounded-full flex items-center transition-all ${
                            cookiePreferences.analytics 
                              ? 'bg-luxury-gold justify-end' 
                              : 'bg-zinc-700 justify-start'
                          } px-0.5 md:px-1`}>
                            <div className="w-3.5 md:w-4 h-3.5 md:h-4 bg-white rounded-full" />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 md:gap-3 sticky bottom-0 bg-gradient-to-t from-black to-transparent pt-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleSavePreferences}
                      className="btn-primary rounded-full px-5 md:px-8 py-2 md:py-3 text-sm md:text-base font-semibold flex-1"
                    >
                      Auswahl speichern
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleAcceptAll}
                      className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-full px-5 md:px-8 py-2 md:py-3 text-sm md:text-base font-semibold flex-1 transition-colors"
                    >
                      Alle akzeptieren
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
